import React, { ReactNode } from "react";

import { Box, Divider, Link, Typography } from "@mui/material";

interface MarkdownTextProps {
  omitTitle?: string;
  source?: string;
}

type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "quote"; text: string }
  | { type: "rule" };

const INLINE_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;

const normalizeTitle = (value: string) => value.replace(/[*_#:]/g, "").replace(/\s+/g, " ").trim().toLowerCase();

const stripFence = (value: string) => value
  .replace(/^```(?:markdown|md)?[ \t]*\n?/i, "")
  .replace(/\n?```[ \t]*$/i, "")
  .trim();

const restoreFlattenedMarkdown = (text: string) => {
  const withBullets = text.replace(/[ \t]([-*])[ \t]+/g, (match, marker: string, offset: number, source: string) => {
    const markerIndex = offset + match.lastIndexOf(marker);
    if (marker === "*" && (source[markerIndex - 1] === "*" || source[markerIndex + 1] === "*")) return match;
    return "\n\n- ";
  });

  const withNumbers = withBullets.replace(/[ \t](\d{1,2})\.[ \t]+(?=[A-Z*])/g, "\n\n$1. ");

  return withNumbers.replace(/[ \t](\*\*[^*\n]+:\*\*)/g, (match, label: string, offset: number, source: string) => {
    const before = source.slice(Math.max(0, offset - 8), offset);
    if (/\d\.\s*$/.test(before) || /(?:^|\s)-\s*$/.test(before) || /(?:^|\s)\*(?!\*)\s*$/.test(before)) return match;
    return `\n\n${label}`;
  }).trim();
};

export const normalizeDescriptionMarkdown = (source: string) => {
  const text = stripFence(source.replace(/\r\n/g, "\n").trim());
  const structured = text.includes("\n") ? text : restoreFlattenedMarkdown(text);
  return structured.replace(/\n{3,}/g, "\n\n").trim();
};

const standaloneLabel = (line: string) => {
  const withColon = line.match(/^\*\*([^*\n]+):\*\*\s*$/);
  if (withColon) return withColon[1].trim();
  const plain = line.match(/^\*\*([^*\n]+)\*\*\s*$/);
  if (plain) return plain[1].trim();
  return null;
};

const parseMarkdown = (source: string): Block[] => {
  const lines = normalizeDescriptionMarkdown(source).split("\n");
  const blocks: Block[] = [];
  const paragraph: string[] = [];

  const flushParagraph = () => {
    const text = paragraph.join(" ").trim();
    paragraph.length = 0;
    if (text) blocks.push({ type: "paragraph", text });
  };

  let index = 0;
  while (index < lines.length) {
    const trimmed = lines[index].trim();

    if (!trimmed) {
      flushParagraph();
      index += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+?)\s*#*$/);
    if (heading) {
      flushParagraph();
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2].trim() });
      index += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      flushParagraph();
      blocks.push({ type: "rule" });
      index += 1;
      continue;
    }

    const listMatch = trimmed.match(/^[-*]\s+(.+)$/) || trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      const ordered = /^\d/.test(trimmed);
      const items: string[] = [];
      while (index < lines.length) {
        const itemLine = lines[index];
        const item = itemLine.trim();
        const matched = ordered ? item.match(/^\d+[.)]\s+(.+)$/) : item.match(/^[-*]\s+(.+)$/);
        if (matched) {
          items.push(matched[1]);
          index += 1;
          continue;
        }
        if (items.length && /^\s{2,}\S/.test(itemLine)) {
          items[items.length - 1] += ` ${item.trim()}`;
          index += 1;
          continue;
        }
        break;
      }
      blocks.push(ordered ? { type: "ol", items } : { type: "ul", items });
      continue;
    }

    if (trimmed.startsWith(">")) {
      flushParagraph();
      const quote: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith(">")) {
        quote.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ type: "quote", text: quote.join(" ").trim() });
      continue;
    }

    const label = standaloneLabel(trimmed);
    if (label) {
      flushParagraph();
      blocks.push({ type: "heading", level: 3, text: label });
      index += 1;
      continue;
    }

    paragraph.push(trimmed);
    index += 1;
  }

  flushParagraph();
  return blocks;
};

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const pattern = new RegExp(INLINE_PATTERN.source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const key = `${keyPrefix}-${index}`;
    if (match[1] && match[2]) {
      nodes.push(
        <Link key={key} href={match[2]} target="_blank" rel="noopener noreferrer">
          {match[1]}
        </Link>
      );
    } else if (match[3]) {
      nodes.push(
        <Typography key={key} component="strong" sx={{ ...inlineSx, color: "text.primary", fontWeight: 600 }}>
          {match[3]}
        </Typography>
      );
    } else if (match[4]) {
      nodes.push(
        <Typography key={key} component="code" sx={{ ...inlineSx, fontFamily: "source-code-pro, Menlo, monospace", fontSize: "0.92em" }}>
          {match[4]}
        </Typography>
      );
    } else if (match[5]) {
      nodes.push(
        <Typography key={key} component="em" sx={inlineSx}>
          {match[5]}
        </Typography>
      );
    }
    lastIndex = match.index + match[0].length;
    index += 1;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const inlineSx = {
  fontSize: "inherit",
  lineHeight: "inherit",
};

const headingSx = (level: number, isFirst: boolean) => ({
  color: "text.primary",
  fontSize: level <= 2 ? { xs: "16px", sm: "18px" } : { xs: "15px", sm: "16px" },
  fontWeight: 600,
  lineHeight: 1.35,
  mt: isFirst ? 0 : 2,
  mb: 0.75,
});

const bodySx = {
  color: "text.secondary",
  fontSize: "inherit",
  lineHeight: 1.65,
  mb: 1.5,
};

const MarkdownText = ({ omitTitle, source }: MarkdownTextProps) => {
  if (!source?.trim()) return null;

  let blocks = parseMarkdown(source);
  if (omitTitle && blocks[0]?.type === "heading" && normalizeTitle(blocks[0].text) === normalizeTitle(omitTitle)) {
    blocks = blocks.slice(1);
  }

  return (
    <Box sx={{
      color: "text.secondary",
      fontSize: { xs: "14px", sm: "16px" },
      lineHeight: 1.65,
    }}>
      {blocks.map((block, index) => {
        const key = `block-${index}`;
        if (block.type === "heading") {
          return (
            <Typography key={key} component="h3" sx={headingSx(block.level, index === 0)}>
              {renderInline(block.text, key)}
            </Typography>
          );
        }
        if (block.type === "rule") return <Divider key={key} sx={{ my: 1.5 }} />;
        if (block.type === "quote") {
          return (
            <Typography key={key} component="blockquote" sx={{ ...bodySx, borderLeft: "3px solid", borderColor: "divider", pl: 1.5, fontStyle: "italic" }}>
              {renderInline(block.text, key)}
            </Typography>
          );
        }
        if (block.type === "ul" || block.type === "ol") {
          return (
            <Box
              key={key}
              component={block.type}
              sx={{ listStyle: block.type === "ol" ? "decimal" : "disc", my: 0.5, mb: 1.5, pl: 3 }}
            >
              {block.items.map((item, itemIndex) => (
                <Box component="li" key={`${key}-${itemIndex}`} sx={{ mb: 0.75, pl: 0.5 }}>
                  {renderInline(item, `${key}-${itemIndex}`)}
                </Box>
              ))}
            </Box>
          );
        }

        const lead = block.text.match(/^\*\*([^*\n]+):\*\*\s+([\s\S]+)$/);
        if (lead) {
          return (
            <React.Fragment key={key}>
              <Typography component="h3" sx={headingSx(3, index === 0)}>{lead[1].trim()}</Typography>
              <Typography sx={bodySx}>{renderInline(lead[2], key)}</Typography>
            </React.Fragment>
          );
        }

        return (
          <Typography key={key} sx={bodySx}>
            {renderInline(block.text, key)}
          </Typography>
        );
      })}
    </Box>
  );
};

export default MarkdownText;
